import React, { useCallback, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { motion } from 'framer-motion'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import type { EmotionType } from '../../../../types/FeedbackTypes'
import EmotionVisualization from './EmotionVisualization'
import { GlintReveal } from '../../components/GlintReveal'
import { useGlowIntersection } from '../../../gamer/hooks/useGlowIntersection'
import { DEFAULT_FADE_DURATION, DEFAULT_GLINT_DURATION, loadGlintDurationConfig } from '../../constants/glintConfig'

const MotionBox = motion(Box)

export interface EmotionStateSectionProps {
  /**
   * Dominant communicated emotion for the session
   */
  emotion: EmotionType | string
  /**
   * Optional example utterance associated with the dominant emotion
   */
  exampleUtterance?: string

  /**
   * Optional generated text describing the communicated emotional tone
   */
  text?: string
  /**
   * Whether to show the glow experience overlay
   */
  showGlowExperience?: boolean
  /**
   * Callback when glow experience is completed (section revealed)
   */
  onGlowExperienceComplete?: () => void
  /**
   * Callback when glint animation state changes
   */
  onGlintStateChange?: (state: 'idle' | 'running') => void
}

/**
 * EmotionStateSection
 *
 * High-level card for the "Your emotional state" section on Latest Session Feedback V2.
 * - Uses EmotionVisualization as the right-side visual
 * - Left side shows text summary and example utterance
 * - Responsive layout
 * - Supports glow/glint reveal experience like other feedback sections
 */
export const EmotionStateSection: React.FC<EmotionStateSectionProps> = ({
  emotion,
  exampleUtterance,
  text,
  showGlowExperience = false,
  onGlowExperienceComplete,
  onGlintStateChange
}) => {
  const { t } = useTranslation()

  const containerRef = useRef<HTMLDivElement>(null)
  const [glintTrigger, setGlintTrigger] = useState(0)
  const [isOverlayActive, setIsOverlayActive] = useState(showGlowExperience)
  const [overlayState, setOverlayState] = useState<'hidden' | 'blur' | 'fade'>(showGlowExperience ? 'blur' : 'hidden')
  const [glintPhase, setGlintPhase] = useState<'idle' | 'running'>('idle')
  const hasRevealedRef = useRef(false)
  const glintCompletedRef = useRef(!showGlowExperience)
  const lastTriggerRef = useRef(glintTrigger)
  const fadeStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const overlayHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const storedDurations = React.useMemo(() => loadGlintDurationConfig(), [])
  const glintDurationSec = storedDurations.glintDuration ?? DEFAULT_GLINT_DURATION
  const fadeDurationSec = storedDurations.fadeDuration ?? DEFAULT_FADE_DURATION
  const experienceDurationSec = storedDurations.experienceDuration ?? glintDurationSec + fadeDurationSec

  const titleText = t('gamer.feedback.emotion.title', 'Your emotional state')

  const descriptionText = t('gamer.feedback.sections.experience.description', {
    defaultValue: 'Your emotional and psychological experience during the session'
  })

  const communicatedEmotionText = (text ?? '').trim()

  // Handle showGlowExperience changes
  React.useEffect(() => {
    if (showGlowExperience) {
      setIsOverlayActive(true)
      setOverlayState('blur')
      setGlintPhase('idle')
      glintCompletedRef.current = false
      hasRevealedRef.current = false
      return
    }

    setIsOverlayActive(false)
    setOverlayState('hidden')
    setGlintPhase('idle')
    glintCompletedRef.current = true
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current)
      overlayHideTimeoutRef.current = null
    }
    if (fadeStartTimeoutRef.current) {
      clearTimeout(fadeStartTimeoutRef.current)
      fadeStartTimeoutRef.current = null
    }
    lastTriggerRef.current = glintTrigger
  }, [showGlowExperience, glintTrigger])

  // Handle glint trigger lifecycle
  React.useEffect(() => {
    if (!showGlowExperience) return
    if (glintTrigger === 0 || glintTrigger === lastTriggerRef.current) return

    setIsOverlayActive(true)
    setOverlayState('blur')
    setGlintPhase('running')
    glintCompletedRef.current = false

    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current)
      overlayHideTimeoutRef.current = null
    }
    if (fadeStartTimeoutRef.current) {
      clearTimeout(fadeStartTimeoutRef.current)
      fadeStartTimeoutRef.current = null
    }

    const fadeStartDelaySec = Math.max(experienceDurationSec - fadeDurationSec, 0)
    const handleFadeStart = () => {
      fadeStartTimeoutRef.current = null
      setOverlayState('fade')
      if (fadeDurationSec <= 0) {
        setOverlayState('hidden')
        setIsOverlayActive(false)
        onGlowExperienceComplete?.()
        return
      }
      overlayHideTimeoutRef.current = setTimeout(() => {
        setOverlayState('hidden')
        setIsOverlayActive(false)
        overlayHideTimeoutRef.current = null
        onGlowExperienceComplete?.()
      }, fadeDurationSec * 1000)
    }

    if (fadeStartDelaySec <= 0) {
      handleFadeStart()
    } else {
      fadeStartTimeoutRef.current = setTimeout(handleFadeStart, fadeStartDelaySec * 1000)
    }

    lastTriggerRef.current = glintTrigger
  }, [experienceDurationSec, fadeDurationSec, glintTrigger, onGlowExperienceComplete, showGlowExperience])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      glintCompletedRef.current = true
      if (overlayHideTimeoutRef.current) {
        clearTimeout(overlayHideTimeoutRef.current)
      }
      if (fadeStartTimeoutRef.current) {
        clearTimeout(fadeStartTimeoutRef.current)
      }
    }
  }, [])

  const handleGlintComplete = useCallback(() => {
    if (glintCompletedRef.current) return
    glintCompletedRef.current = true
    setGlintPhase('idle')
  }, [])

  React.useEffect(() => {
    onGlintStateChange?.(glintPhase)
  }, [glintPhase, onGlintStateChange])

  // Trigger glint when cursor enters the section
  useGlowIntersection(containerRef, {
    enabled: showGlowExperience,
    onEnter: () => {
      if (!hasRevealedRef.current && glintPhase !== 'running') {
        setGlintTrigger((prev) => prev + 1)
      }
    }
  })

  const handleMouseEnter = useCallback(() => {
    if (showGlowExperience && !hasRevealedRef.current && glintPhase !== 'running') {
      setGlintTrigger((prev) => prev + 1)
    }
  }, [showGlowExperience, glintPhase])

  const handleFocus = useCallback(() => {
    if (showGlowExperience && !hasRevealedRef.current && glintPhase !== 'running') {
      setGlintTrigger((prev) => prev + 1)
    }
  }, [showGlowExperience, glintPhase])

  return (
    <Box
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
        border: `1px solid ${INTERAX_COLORS.white08}`,
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(6px)',
        borderRadius: '16px',
        padding: { xs: '16px', sm: '20px', md: '30px' },
        gap: { xs: '12px', sm: '16px', md: '24px' },
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'flex-start' },
          justifyContent: { xs: 'center', sm: 'center', md: 'space-between' },
          gap: { xs: 2, sm: 3, md: 4 },
          flex: 1,
          boxSizing: 'border-box',
          minWidth: 0
        }}
      >
        {/* Content area: centered text on left, visualization on right */}

        {/* Left side - centered text content */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: '100%',
            width: { xs: '100%', sm: '100%', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            alignItems: { xs: 'center', sm: 'center', md: 'flex-start' },
            textAlign: { xs: 'center', sm: 'center', md: 'left' },
            zIndex: 0,
            overflow: 'visible',
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'flex-start' },
              zIndex: 0,
              boxSizing: 'border-box'
            }}
          >
            {/* Title at the top */}
            <Typography
              sx={{
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '100%',
                fontFamily: 'Lexend Deca',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '26px',
                lineHeight: '34px',
                background: INTERAX_COLORS.gradientGoldenTitle,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)',
                margin: 0,
                textAlign: { xs: 'center', sm: 'center', md: 'left' },
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              {titleText}
            </Typography>
          </Box>
          <Typography
            sx={{
              ...INTERAX_TYPOGRAPHY.body2,
              color: INTERAX_COLORS.typographyFont,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              boxSizing: 'border-box'
            }}
          >
            {communicatedEmotionText || descriptionText}
          </Typography>
        </Box>

        {/* Right side emotion visualization */}
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: '100%', sm: '100%', md: 'auto' },
            maxWidth: { xs: '100%', sm: '100%', md: 'none' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 0
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: '100%', md: 'auto' },
              maxWidth: { xs: '100%', sm: '100%', md: '307px' },
              height: { xs: 'auto', sm: 'auto', md: '308px' },
              minHeight: { xs: 'auto', sm: 'auto', md: '308px' }
            }}
          >
            <EmotionVisualization
              emotion={emotion}
              title={t('gamer.feedback.emotions.title', 'Communicated Feeling')}
              variant="individual"
              text={''}
              exampleUtterance={exampleUtterance}
            />
          </Box>
        </Box>

        {/* Blur Overlay - covers content and blurs it */}
        {isOverlayActive && (
          <MotionBox
            sx={{
              margin: -20,
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              background: `linear-gradient(148deg, ${INTERAX_COLORS.heritageBlue40} 0%, ${INTERAX_COLORS.white08} 100%)`,
              backdropFilter: 'blur(12px)',
              zIndex: 1
            }}
            initial={{ opacity: 1 }}
            animate={{ opacity: overlayState === 'fade' ? 0 : 1 }}
            transition={{
              duration: overlayState === 'fade' ? Math.max(fadeDurationSec, 0.01) : 0.1,
              ease: 'easeInOut'
            }}
          />
        )}

        {/* Glint Reveal Animation - on top for shine effect */}
        {showGlowExperience && (
          <Box sx={{ position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none', zIndex: 2 }}>
            <GlintReveal
              active={glintPhase === 'running'}
              trigger={glintTrigger}
              glintDuration={glintDurationSec}
              onComplete={handleGlintComplete}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default EmotionStateSection
